<template>
  <form-layout
    class="contact-form"
    :class="{ 'partner-or-vendor': typeIsPartnerOrVendor }"
    entity-name="contact"
    :loading="isLoading"
    @save="saveContact"
  >
    <template #title>
      <h1 class="uppercase">{{ titleText }}</h1>
    </template>
    <template #body>
      <bdg-dropdown
        ref="type"
        class="type"
        name="Type"
        :options="types"
        v-model="formData.type"
        required
      />

      <bdg-dropdown
        ref="subtype"
        class="subtype"
        name="Subtype"
        :options="subtypes"
        v-model="formData.subtype"
        v-if="subtypes"
        required
      />

      <div class="names">
        <bdg-text-field
          ref="name"
          class="name"
          name="Contact name"
          v-model="formData.name"
          :required="!typeIsPartnerOrVendor"
        />

        <bdg-text-field
          ref="companyName"
          class="company-name"
          name="Company name"
          v-model="formData.company_name"
          :required="typeIsPartnerOrVendor"
        />
      </div>

      <bdg-text-field name="Title" v-model="formData.title" />

      <bdg-aside title="Primary address" />

      <bdg-address-fields
        ref="place"
        v-model="formData.primary_place"
        required
      />

      <template v-for="(place, i) in formData.additional_places">
        <bdg-aside
          :key="`address_aside_${i}`"
          :title="`Additional address ${i + 1}`"
          @remove="removePlace(i)"
          removable
        />

        <bdg-address-fields
          v-model="formData.additional_places[i]"
          :key="`additional_address_${i}`"
        />
      </template>

      <bdg-add-field
        class="mb-3"
        add-text="Add address"
        v-model="formData.additional_places"
        :new-value="newPlace()"
      />

      <bdg-text-field name="Email" v-model="formData.email" />

      <bdg-phone-number-field
        ref="primaryPhone"
        name="Primary Phone"
        v-model="formData.primary_phone"
      />

      <bdg-phone-number-field
        ref="additionalPhones"
        name="Additional Phone"
        @remove="removePhoneNumber(i)"
        v-model="formData.additional_phones[i]"
        v-for="(phone, i) in formData.additional_phones"
        :key="`phone_${i}`"
        removable
      />

      <bdg-add-field
        class="mb-3"
        add-text="Add phone number"
        v-model="formData.additional_phones"
        :new-value="newPhoneNumber()"
      />

      <bdg-text-field name="Website URL" v-model="formData.website_url" />
      <bdg-text-field name="Keywords" v-model="formData.keywords" />

      <bdg-text-area name="Note" v-model="formData.note.text" />
    </template>
  </form-layout>
</template>

<script>
import FormLayout from "../views/layouts/FormLayout";
import BdgTextField from "./fields/BdgTextField";
import BdgDropdown from "./fields/BdgDropdown";
import BdgAside from "../elements/BdgAside";
import BdgPhoneNumberField from "./fields/BdgPhoneNumberField";
import BdgAddField from "./fields/BdgAddField";
import BdgTextArea from "./fields/BdgTextArea";
import { Place } from "../models/Place";
import { Phone } from "../models/Phone";
import BdgAddressFields from "./fields/BdgAddressFields";
import {
  ContactTypeLabels,
  ContactTypes,
  InternalSubtypeLabels,
  InternalSubtypes,
  VendorSubtypeLabels,
  VendorSubtypes
} from "../constants/contact-enums";
import { validateRequiredRefs } from "../helpers";

export default {
  name: "ContactForm",
  components: {
    BdgAddressFields,
    BdgTextArea,
    BdgAddField,
    BdgPhoneNumberField,
    BdgAside,
    BdgDropdown,
    BdgTextField,
    FormLayout
  },
  props: {
    id: Number,
    name: { type: String, default: "" },
    type: { type: String, default: "" },
    subtype: String,
    company_name: { type: String, default: "" },
    title: { type: String, default: "" },
    primary_place: { type: Place, default: () => new Place() },
    additional_places: { type: Array, default: () => [] },
    email: { type: String, default: "" },
    website_url: { type: String },
    keywords: { type: String },
    primary_phone: {
      type: Phone,
      default: () => new Phone()
    },
    additional_phones: { type: Array, default: () => [] },
    note: {
      type: Object,
      default: () => {
        return { text: null };
      }
    }
  },
  data() {
    return {
      formData: {
        id: this.id,
        name: this.name,
        type: this.type,
        subtype: this.subtype,
        company_name: this.company_name,
        title: this.title,
        primary_place: this.primary_place.clone(),
        additional_places: this.additional_places.slice(),
        email: this.email,
        primary_phone: this.primary_phone,
        additional_phones: this.additional_phones.slice(),
        website_url: this.website_url,
        keywords: this.keywords,
        note: Object.assign({}, this.note)
      },
      types: Object.values(ContactTypes).map(slug => {
        return { label: ContactTypeLabels[slug], value: slug };
      })
    };
  },
  computed: {
    titleText() {
      return this.id ? "Edit Contact" : "Create New Contact";
    },
    subtypes() {
      if (
        this.formData.type === ContactTypes.PARTNER ||
        this.formData.type === ContactTypes.VENDOR
      ) {
        return Object.values(VendorSubtypes).map(slug => {
          return { label: VendorSubtypeLabels[slug], value: slug };
        });
      } else if (this.formData.type === ContactTypes.INTERNAL) {
        return Object.values(InternalSubtypes).map(slug => {
          return { label: InternalSubtypeLabels[slug], value: slug };
        });
      }
      return null;
    },
    typeIsPartnerOrVendor() {
      return (
        this.formData.type === ContactTypes.VENDOR ||
        this.formData.type === ContactTypes.PARTNER
      );
    },
    requiredRefs() {
      const refs = ["name", "type", "place"];

      if (this.subtypes) {
        refs.push("subtype");
      }

      if (
        this.formData.primary_phone &&
        (this.formData.primary_phone.number || this.formData.primary_phone.type)
      ) {
        refs.push("primaryPhone");
      }

      if (
        this.formData.additional_phones &&
        this.formData.additional_phones.length
      ) {
        refs.push("additionalPhones");
      }

      if (this.typeIsPartnerOrVendor) {
        refs.push("companyName");
        refs.splice(refs.indexOf("name"), 1);
      }

      return refs;
    },
    isStoringContact() {
      return this.$store.state.Contacts.isStoringContact;
    },
    isUpdatingContact() {
      return this.$store.state.Contacts.isUpdatingContact;
    },
    isLoading() {
      return this.id ? this.isUpdatingContact : this.isStoringContact;
    }
  },
  methods: {
    newPlace() {
      return new Place();
    },
    newPhoneNumber() {
      return new Phone();
    },
    removePlace(index) {
      this.formData.additional_places.splice(index, 1);
    },
    removePhoneNumber(index) {
      this.formData.additional_phones.splice(index, 1);
    },
    validate() {
      return validateRequiredRefs(this, true);
    },
    preSubmit() {
      if (
        this.formData.additional_places &&
        this.formData.additional_places.length
      ) {
        this.formData.additional_places = this.formData.additional_places.filter(
          p => {
            return p.address_1 && p.city && p.state && p.zip;
          }
        );
      }
    },
    saveContact() {
      if (!this.validate()) {
        return;
      }

      this.preSubmit();

      if (this.id) {
        this.$store.dispatch("updateContact", {
          ...this.formData,
          isShowView: this.$route.name === "contact-show"
        });
      } else {
        this.$store.dispatch("storeContact", this.formData);
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.contact-form {
  &:not(.partner-or-vendor) {
    .body {
      .names {
        .bdg-input {
          &.name {
            order: 1;
          }

          &.company-name {
            order: 2;
          }
        }
      }
    }
  }

  &.partner-or-vendor {
    .body {
      .names {
        .bdg-input {
          &.name {
            order: 2;
          }

          &.company-name {
            order: 1;
          }
        }
      }
    }
  }

  .body {
    .names {
      @apply flex flex-col;

      .bdg-input {
        @apply mt-4;
      }
    }
  }
}
</style>
